/**
 * AI 生产用内容主题目录（防题材重复）。
 *
 * 分层（刻意，镜像 levelOptions）：
 * - 目录：按 exam + 可选 Skill section
 * - 无目录的 Scope：生题不注入主题约束（omit = 不限）
 * - **情境主题 id 跨 Language/Exam 通用**（UNIVERSAL_SITUATION_TOPICS）；
 *   学术加料仅挂部分听读 Scope
 * - 产线词表 + 题面/历史 label；不是 Platform Scope，不是学员入口导航
 */

export type ContentTopic = {
  /** 稳定 id，写入题目 topics[]（全语言共用） */
  id: string;
  /** 运营 / prompt 展示名（中文） */
  label: string;
};

/**
 * 全语言通用日常/考试情境主题。
 * TCF / IELTS / TOEFL / DELE / SIELE / JLPT / TOPIK 共用同一套 id。
 */
const UNIVERSAL_SITUATION_TOPICS: readonly ContentTopic[] = [
  { id: 'daily_life', label: '日常生活' },
  { id: 'family_friends', label: '家庭与朋友' },
  { id: 'housing', label: '住房与居住' },
  { id: 'food_dining', label: '饮食与餐馆' },
  { id: 'shopping', label: '购物与消费' },
  { id: 'health', label: '健康与医疗' },
  { id: 'education', label: '学习与教育' },
  { id: 'work_career', label: '工作与职业' },
  { id: 'travel_transport', label: '出行与交通' },
  { id: 'leisure_culture', label: '休闲与文化' },
  { id: 'media_tech', label: '媒体与科技' },
  { id: 'environment', label: '环境与自然' },
  { id: 'civic_society', label: '社会与公民生活' },
] as const;

/** 学术加料（叠在通用情境上；id 仍全局唯一） */
const ACADEMIC_EXTRA: readonly ContentTopic[] = [
  { id: 'campus_life', label: '校园生活' },
  { id: 'academic_lecture', label: '学术讲座/课堂' },
  { id: 'research_science', label: '科研与科学' },
] as const;

const WITH_ACADEMIC: readonly ContentTopic[] = [
  ...UNIVERSAL_SITUATION_TOPICS,
  ...ACADEMIC_EXTRA,
] as const;

type ExamTopicConfig = {
  /** 未单独配置 section 时的回退目录；缺省且无 bySection 命中 → 无主题约束 */
  default?: readonly ContentTopic[];
  bySection?: Readonly<Record<string, readonly ContentTopic[]>>;
};

const EXAM_TOPIC_MAP: Readonly<Record<string, ExamTopicConfig>> = {
  tcf: {
    default: UNIVERSAL_SITUATION_TOPICS,
    bySection: {
      // Align with TCF_SECTIONS (no speaking Skill registered yet)
      writing: UNIVERSAL_SITUATION_TOPICS,
      listening: UNIVERSAL_SITUATION_TOPICS,
      reading: UNIVERSAL_SITUATION_TOPICS,
      language_structures: UNIVERSAL_SITUATION_TOPICS,
    },
  },
  ielts: {
    default: UNIVERSAL_SITUATION_TOPICS,
    bySection: {
      writing: UNIVERSAL_SITUATION_TOPICS,
      speaking: UNIVERSAL_SITUATION_TOPICS,
      listening: WITH_ACADEMIC,
      reading: WITH_ACADEMIC,
    },
  },
  toefl: {
    default: WITH_ACADEMIC,
    bySection: {
      writing: WITH_ACADEMIC,
      speaking: WITH_ACADEMIC,
      listening: WITH_ACADEMIC,
      reading: WITH_ACADEMIC,
    },
  },
  dele: {
    default: UNIVERSAL_SITUATION_TOPICS,
    bySection: {
      writing: UNIVERSAL_SITUATION_TOPICS,
      speaking: UNIVERSAL_SITUATION_TOPICS,
      listening: UNIVERSAL_SITUATION_TOPICS,
      reading: UNIVERSAL_SITUATION_TOPICS,
    },
  },
  siele: {
    default: UNIVERSAL_SITUATION_TOPICS,
    bySection: {
      writing: UNIVERSAL_SITUATION_TOPICS,
      speaking: UNIVERSAL_SITUATION_TOPICS,
      listening: UNIVERSAL_SITUATION_TOPICS,
      reading: UNIVERSAL_SITUATION_TOPICS,
    },
  },
  jlpt: {
    default: UNIVERSAL_SITUATION_TOPICS,
    bySection: {
      vocabulary: UNIVERSAL_SITUATION_TOPICS,
      grammar_reading: UNIVERSAL_SITUATION_TOPICS,
      listening: UNIVERSAL_SITUATION_TOPICS,
    },
  },
  topik: {
    default: UNIVERSAL_SITUATION_TOPICS,
    bySection: {
      writing: UNIVERSAL_SITUATION_TOPICS,
      listening: UNIVERSAL_SITUATION_TOPICS,
      reading: UNIVERSAL_SITUATION_TOPICS,
    },
  },
};

function normalizeExamType(examType: string): string {
  return examType.trim().toLowerCase();
}

function normalizeSection(section?: string | null): string | null {
  const trimmed = section?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/** 该 Scope 的受控主题目录；空数组 = 不注入主题约束 */
export function getContentTopicsForScope(
  examType: string,
  section?: string | null
): ContentTopic[] {
  const exam = EXAM_TOPIC_MAP[normalizeExamType(examType)];
  if (!exam) return [];

  const sec = normalizeSection(section);
  if (sec && exam.bySection?.[sec]) {
    return [...exam.bySection[sec]];
  }
  if (exam.default) return [...exam.default];
  return [];
}

export function isContentTopicSupported(
  examType: string,
  section: string | null | undefined,
  topicId: string
): boolean {
  const id = topicId.trim().toLowerCase();
  if (!id) return false;
  return getContentTopicsForScope(examType, section).some((t) => t.id === id);
}

/**
 * Parse optional content-topic filter from query/UI.
 * Empty / unsupported → undefined (any topic).
 */
export function normalizeOptionalContentTopicId(
  examType: string,
  section: string | null | undefined,
  raw: string | null | undefined
): string | undefined {
  const id = raw?.trim().toLowerCase();
  if (!id) return undefined;
  return isContentTopicSupported(examType, section, id) ? id : undefined;
}

/** Admin / prompt 下拉：value=id, label=展示名 */
export function getContentTopicOptionsForScope(
  examType: string,
  section?: string | null
): Array<{ value: string; label: string }> {
  return getContentTopicsForScope(examType, section).map((t) => ({
    value: t.id,
    label: t.label,
  }));
}

/**
 * 将模型/导入侧原始 topics 收敛到本 Scope 词表。
 * 未知 id 丢弃；空则 undefined。
 */
export function normalizeTopicsForScope(
  examType: string,
  section: string | null | undefined,
  raw: unknown
): string[] | undefined {
  const catalog = getContentTopicsForScope(examType, section);
  if (catalog.length === 0) return undefined;

  const allowed = new Set(catalog.map((t) => t.id));
  const parts: string[] = [];
  const push = (value: string) => {
    const id = value.trim().toLowerCase();
    if (!id || !allowed.has(id) || parts.includes(id)) return;
    parts.push(id);
  };

  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string') push(item);
    }
  } else if (typeof raw === 'string') {
    for (const part of raw.split(/[,，;；\s]+/)) push(part);
  }

  return parts.length > 0 ? parts : undefined;
}
